import type {
  ApiErrorBody,
  Ballot,
  CreateVoteResponse,
  GetResultsResponse,
  GetVoteResponse,
  SubmitBallotResponse,
  VoteMode,
} from "../../shared/types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    });
  } catch {
    throw new ApiError(0, "Fikk ikke kontakt med serveren.");
  }
  const body = (await response.json().catch(() => null)) as
    | (ApiErrorBody & Record<string, unknown>)
    | null;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof body?.error === "string" ? body.error : "Noe gikk galt.",
      Array.isArray(body?.details) ? body.details : undefined,
    );
  }
  return body as T;
}

export interface CreateVotePayload {
  title: string;
  options: string[];
  mode: VoteMode;
  maxDots?: number;
}

export const api = {
  createVote: (payload: CreateVotePayload) =>
    request<CreateVoteResponse>("/api/votes", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getVote: (voteId: string) =>
    request<GetVoteResponse>(`/api/votes/${encodeURIComponent(voteId)}`),
  submitBallot: (voteId: string, ballot: Ballot) =>
    request<SubmitBallotResponse>(
      `/api/votes/${encodeURIComponent(voteId)}/ballot`,
      { method: "POST", body: JSON.stringify(ballot) },
    ),
  getResults: (resultsId: string) =>
    request<GetResultsResponse>(
      `/api/results/${encodeURIComponent(resultsId)}`,
    ),
};
